import ReactDatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import React, {
  ButtonHTMLAttributes,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { range } from "lodash";
import ko from "date-fns/locale/ko";
import it from "date-fns/locale/it";
import en_US from "date-fns/locale/en-US";
import AppSelect from "@/features/line-sheet-sets/app-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppDatePickerHeader from "@/features/line-sheet-sets/app-date-picker-header";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

registerLocale("ko", ko);
registerLocale("it", it);
registerLocale("en", en_US);

interface AppDateRangeSelectInputProps
  extends React.DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  // 값이 있으면 "시작 ~ 종료", 없으면 필터 이름.
  label: string;
  hasValue: boolean;
  width: string;
  onInputClick: () => void;
  onClear: () => void;
  isDisabled?: boolean;
  isReadOnly?: boolean;
}

// 트리거는 다른 필터와 같은 모양(AppSelect)으로 둔다. 옵션이 비어 있어 자체 팝오버는 열리지 않고,
// 클릭은 달력 토글로만 쓰인다 — AppDatePicker의 커스텀 인풋과 같은 방식.
function AppDateRangeSelectInput(
  props: AppDateRangeSelectInputProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  return (
    <AppSelect<string>
      name={props.label}
      value={props.hasValue ? props.label : undefined}
      options={[]}
      width={props.width}
      isDisabled={props.isDisabled}
      isReadOnly={props.isReadOnly}
      buttonRef={ref}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (props.onClick) {
          props.onClick(event);
        }
        props.onInputClick();
      }}
      onSelect={(value) => {
        if (value === undefined) {
          props.onClear();
        }
      }}
    />
  );
}

const ForwardedAppDateRangeSelectInput = forwardRef(AppDateRangeSelectInput);

interface AppDateRangeSelectProps {
  value?: {
    from?: Date;
    to?: Date;
  };
  onChange: (value: { from?: Date; to?: Date }) => void;
  /** 값이 없을 때 버튼에 표시되는 필터 이름 */
  name: string;
  width?: string;
  minDate?: Date;
  maxDate?: Date;
  isDisabled?: boolean;
  isReadOnly?: boolean;
}

/**
 * 버튼 하나로 기간을 고르는 필터. 누르면 달력이 열리고 시작일 → 종료일 순으로 찍으면 닫힌다.
 * 시작/종료 인풋을 따로 두는 AppDateRangePicker와 달리 필터 줄에서 자리를 하나만 차지한다.
 */
export default function AppDateRangeSelect({
  value,
  onChange,
  name,
  width = "216px",
  minDate,
  maxDate,
  isDisabled = false,
  isReadOnly = false,
}: AppDateRangeSelectProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // 달력에서 고르는 중인 값. 적용을 눌러야 바깥으로 나가므로 시작일만 찍은 반쪽 상태로
  // 목록이 다시 불려가지 않는다.
  const [draft, setDraft] = useState<{ from?: Date; to?: Date }>({});
  const customInputRef = useRef<HTMLButtonElement>(null);
  const { tTitle, i18n, tLocalDateString } = useI18nHelper();

  const from = value?.from;
  const to = value?.to;

  const openCalendar = () => {
    setDraft({ from, to });
    setIsOpen(true);
  };

  const locale = useMemo(() => {
    if (i18n.language === "ko") {
      return ko;
    } else if (i18n.language === "it") {
      return it;
    } else {
      return en_US;
    }
  }, [i18n.language]);

  // 시작만 고른 중간 상태도 그대로 보여준다("2026-08-01 ~").
  const formatRange = (range: { from?: Date; to?: Date }) =>
    `${tLocalDateString(range.from)} ~ ${tLocalDateString(range.to)}`;

  const label = useMemo(() => {
    if (!from && !to) {
      return name;
    }
    return formatRange({ from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, name, tLocalDateString]);

  useEffect(() => {
    if (isOpen) {
      const elements = document.getElementsByClassName(
        "react-datepicker__day--selected"
      );
      if (elements.length > 0) {
        (elements[0] as HTMLInputElement).focus();
      }
    } else {
      if (customInputRef.current) {
        customInputRef.current.focus();
      }
    }
  }, [isOpen]);

  return (
    <Box width={width}>
      <ReactDatePicker
        popperProps={{
          strategy: "fixed",
        }}
        id={"app-date-range-select"}
        selectsRange={true}
        startDate={draft.from}
        endDate={draft.to}
        minDate={minDate}
        maxDate={maxDate}
        showPopperArrow={false}
        open={isOpen}
        shouldCloseOnSelect={false}
        openToDate={draft.from || draft.to || undefined}
        onChange={(dates) => {
          const [start, end] = dates as [Date | null, Date | null];
          setDraft({
            from: start || undefined,
            to: end || undefined,
          });
        }}
        customInput={
          <ForwardedAppDateRangeSelectInput
            ref={customInputRef}
            label={label}
            hasValue={Boolean(from || to)}
            width={width}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            onInputClick={() => {
              if (isOpen) {
                setIsOpen(false);
              } else {
                openCalendar();
              }
            }}
            onClear={() => {
              onChange({});
            }}
          />
        }
        customInputRef={"ref"}
        locale={locale}
        dateFormat={"yyyy-MM-dd"}
        onClickOutside={(event) => {
          const target = event.target as HTMLDivElement;

          if (
            [target, target.parentElement].some((element) => {
              return element && element.ariaLabel === "clear";
            })
          ) {
            onChange({});
          }

          setIsOpen(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setIsOpen(false);
          }
        }}
        renderCustomHeader={(props) => {
          const months = range(0, 12).map((i) => locale.localize!.month(i));
          return (
            <AppDatePickerHeader
              {...props}
              name={name}
              months={months}
              locale={locale}
              setIsOpen={setIsOpen}
              minDate={minDate}
              maxDate={maxDate}
              showMonthNavigation={true}
            />
          );
        }}
      >
        {/* 고른 범위를 확인하고 적용하는 자리. 닫기(바깥 클릭·ESC·X)로 나가면 draft는 버려진다. */}
        <Box
          display={"inline-flex"}
          flexDirection={"column"}
          gap={"8px"}
          w={"100%"}
          padding={"8px 0 4px 0"}
          borderTop={"0.5px solid #D4D4D1"}
        >
          <Box
            background={"#F7F9FA"}
            border={"0.5px solid #D4D4D1"}
            borderRadius={"4px"}
            padding={"6px 8px"}
          >
            <Text
              fontSize={"13px"}
              fontWeight={"700"}
              textAlign={"center"}
              color={draft.from || draft.to ? "#1272EF" : "#8F8F8C"}
            >
              {draft.from || draft.to ? formatRange(draft) : "-"}
            </Text>
          </Box>
          <Flex flexDirection={"row"} justifyContent={"center"} gap={"8px"}>
            <AppButton
              size={"small"}
              variant={"white"}
              isDisabled={!draft.from && !draft.to}
              onClick={() => setDraft({})}
            >
              {tTitle("reset")}
            </AppButton>
            <AppButton
              size={"small"}
              variant={"primary"}
              onClick={() => {
                onChange(draft);
                setIsOpen(false);
              }}
            >
              {tTitle("apply")}
            </AppButton>
          </Flex>
        </Box>
      </ReactDatePicker>
    </Box>
  );
}
